import React from "react";
import { Package, Calendar, Truck, FileText, Download, Box } from "lucide-react";
import { UoMService } from "src/services";
import { DD_MM_YYYY } from "src/helpers";
import { POAdditionalInfo, POShipmentDetails } from "src/types";
import { useUoM } from "src/hooks";
import { ShipmentUnitTable } from "src/components/tables";
import { NoteDisplay, TrackingLink } from "src/components";
import { CollapsibleSection } from "src/components/ui/collapsible";

interface PurchaseOrderShipmentSectionProps extends POShipmentDetails {
  uomService: UoMService;
  shipmentNote?: POAdditionalInfo["shipmentNote"];
  downloadPackingLists: () => void;
  isFileDownloading?: boolean;
}

const PurchaseOrderShipmentSection: React.FC<PurchaseOrderShipmentSectionProps> = ({
  trackingNo,
  trackingAddedAt,
  shipmentUnitsAddedAt,
  carrier,
  carrierStatus,
  carrierEstimatedDeliveryDate,
  hasPackingList,
  shipmentUnits,
  estimatedDeliveryDate,
  uomService,
  shipmentNote,
  collectionOrder,
  downloadPackingLists,
  isFileDownloading
}) => {
  const [shipmentUnitsUoM, setShipmentUnitsUoM] = useUoM(uomService);

  const displayShipmentDetails =
    trackingNo ||
    carrier ||
    carrierStatus ||
    hasPackingList ||
    (shipmentUnits && shipmentUnits.length > 0) ||
    estimatedDeliveryDate ||
    carrierEstimatedDeliveryDate ||
    shipmentNote ||
    collectionOrder ||
    trackingAddedAt ||
    shipmentUnitsAddedAt;

  if (!displayShipmentDetails) {
    return null;
  }
  return (
    <div className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden">
      <CollapsibleSection
        initiallyOpen={true}
        title={
          <h3 className="text-lg font-semibold text-secondary-800 flex items-center">
            <Truck className="w-5 h-5 mr-2 text-primary-600" />
            Shipment Details
          </h3>
        }
      >
        <div className="p-6 space-y-6">
          {/* Tracking & Carrier Info */}
          {(trackingNo || carrier || carrierStatus) && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {trackingNo && (
                <div className="bg-secondary-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Package className="w-4 h-4 text-primary-600" />
                    <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">
                      Tracking Number
                    </span>
                  </div>
                  <TrackingLink trackingNo={trackingNo} carrier={carrier} collectionOrder={collectionOrder} />
                </div>
              )}
              {carrier && (
                <div className="bg-secondary-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Truck className="w-4 h-4 text-primary-600" />
                    <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">Carrier</span>
                  </div>
                  <p className="text-sm font-medium text-secondary-900">{carrier}</p>
                </div>
              )}
              {carrierStatus && (
                <div className="bg-secondary-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Truck className="w-4 h-4 text-primary-600" />
                    <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">
                      Delivery Status
                    </span>
                  </div>
                  <p className="text-sm font-medium text-secondary-900">{carrierStatus}</p>
                </div>
              )}
            </div>
          )}

          {/* Dates */}
          {(trackingAddedAt || shipmentUnitsAddedAt || carrierEstimatedDeliveryDate || estimatedDeliveryDate) && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {trackingAddedAt && (
                <div className="bg-secondary-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Calendar className="w-4 h-4 text-primary-600" />
                    <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">
                      Tracking Added
                    </span>
                  </div>
                  <p className="text-sm font-medium text-secondary-900">
                    {DD_MM_YYYY.formatDate(new Date(trackingAddedAt))}
                  </p>
                </div>
              )}
              {shipmentUnitsAddedAt && (
                <div className="bg-secondary-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Calendar className="w-4 h-4 text-primary-600" />
                    <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">
                      Dimensions & Weight Added
                    </span>
                  </div>
                  <p className="text-sm font-medium text-secondary-900">
                    {DD_MM_YYYY.formatDate(new Date(shipmentUnitsAddedAt))}
                  </p>
                </div>
              )}
              {carrierEstimatedDeliveryDate && (
                <div className="bg-secondary-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Calendar className="w-4 h-4 text-primary-600" />
                    <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">
                      Carrier Est. Delivery
                    </span>
                  </div>
                  <p className="text-sm font-medium text-secondary-900">
                    {DD_MM_YYYY.formatDate(new Date(carrierEstimatedDeliveryDate))}
                  </p>
                </div>
              )}
              {estimatedDeliveryDate && (
                <div className="bg-secondary-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Calendar className="w-4 h-4 text-primary-600" />
                    <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">
                      Est. Delivery Date
                    </span>
                  </div>
                  <p className="text-sm font-medium text-secondary-900">
                    {DD_MM_YYYY.formatDate(new Date(estimatedDeliveryDate))}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Shipment Units Table */}
          {shipmentUnits && shipmentUnits.length > 0 && (
            <div className="rounded-2xl border border-secondary-200 bg-white p-6 shadow-soft">
              <div className="flex flex-col gap-3 border-b border-secondary-100 pb-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <Box className="h-5 w-5 text-primary-600" />
                  <h4 className="text-base font-semibold text-secondary-900">Shipment Units</h4>
                </div>
                <p className="text-sm text-secondary-600">
                  Dimensions converted automatically between metric and imperial units.
                </p>
              </div>
              <div className="pt-4">
                <ShipmentUnitTable
                  shipmentsUnits={shipmentUnits}
                  shipmentUnitsUoM={shipmentUnitsUoM}
                  setShipmentUnitsUoM={setShipmentUnitsUoM}
                />
              </div>
            </div>
          )}

          {/* Shipping Note */}
          {shipmentNote && (
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <FileText className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-secondary-800 mb-2">Shipping Note (Vendor)</p>
                  <div className="text-sm text-secondary-700 leading-relaxed">
                    <NoteDisplay note={shipmentNote} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Packing List & Collection Order */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {hasPackingList && (
              <div className="bg-secondary-50 rounded-lg p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center space-x-2">
                    <Download className="w-4 h-4 text-primary-600" />
                    <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">
                      Packing Lists
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={downloadPackingLists}
                    disabled={isFileDownloading}
                    className="inline-flex items-center justify-center rounded-lg bg-secondary-900 px-4 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-secondary-800 focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-secondary-300 disabled:text-secondary-500"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    {isFileDownloading ? "Preparing..." : "Download"}
                  </button>
                </div>
              </div>
            )}
            {collectionOrder && (
              <div className="bg-secondary-50 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <Package className="w-4 h-4 text-primary-600" />
                  <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">
                    Collection Order
                  </span>
                </div>
                <p className="text-sm font-medium text-success-600 mt-2">Yes</p>
              </div>
            )}
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
};

export { PurchaseOrderShipmentSection };
